import React from 'react';
import { Bookmark, BookmarkCheck, Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTopicBookmarks } from '@/hooks/useTopicBookmarks';
import { useAuth } from '@/hooks/useAuth';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface BookmarkButtonProps {
  topicId: string;
  variant?: 'default' | 'icon';
  showCount?: boolean;
}

export const BookmarkButton: React.FC<BookmarkButtonProps> = ({ 
  topicId, 
  variant = 'default',
  showCount = false 
}) => {
  const { user } = useAuth();
  const { isBookmarked, bookmarkCount, toggleBookmark, isToggling } = useTopicBookmarks(topicId);
  
  if (!user) return null;

  const handleToggleBookmark = () => {
    toggleBookmark(topicId);
  };

  if (variant === 'icon') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleBookmark}
              disabled={isToggling}
              className="h-8 w-8 p-0"
            >
              {isBookmarked ? (
                <BookmarkCheck className="h-4 w-4 text-primary" />
              ) : (
                <Bookmark className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isBookmarked ? 'Remove bookmark' : 'Bookmark topic'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant={isBookmarked ? "default" : "outline"}
      size="sm"
      onClick={handleToggleBookmark}
      disabled={isToggling}
      className="flex items-center gap-2"
    >
      {isBookmarked ? (
        <BookmarkCheck className="h-4 w-4" />
      ) : (
        <Bookmark className="h-4 w-4" />
      )}
      {isBookmarked ? 'Bookmarked' : 'Bookmark'}
      {showCount && bookmarkCount > 0 && (
        <span className="text-xs opacity-75">({bookmarkCount})</span>
      )}
    </Button>
  );
};